import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiSave, FiClock } from 'react-icons/fi';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const HoursManager = () => {
  const [hours, setHours] = useState({
    monday: { open: '', close: '', closed: true },
    tuesday: { open: '10:00', close: '20:00', closed: false },
    wednesday: { open: '10:00', close: '20:00', closed: false },
    thursday: { open: '10:00', close: '20:00', closed: false },
    friday: { open: '10:00', close: '12:00', closed: false },
    saturday: { open: '10:00', close: '00:00', closed: false },
    sunday: { open: '', close: '', closed: true },
  });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHours();
  }, []);

  const loadHours = async () => {
    try {
      const docRef = doc(db, 'shop', 'hours');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setHours(docSnap.data());
      }
    } catch (error) {
      console.error('Error loading hours:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await setDoc(doc(db, 'shop', 'hours'), hours);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving hours:', error);
      alert('Failed to save hours. Please try again.');
    }
  };

  const handleToggleClosed = (day) => {
    setHours({
      ...hours,
      [day]: { ...hours[day], closed: !hours[day].closed }
    });
  };

  const handleTimeChange = (day, field, value) => {
    setHours({
      ...hours,
      [day]: { ...hours[day], [field]: value }
    });
  };

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white">Loading hours...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <FiClock className="text-2xl sm:text-3xl text-white" />
        <h2 className="text-xl sm:text-2xl font-bold text-white">Business Hours</h2>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {days.map((day) => (
          <motion.div
            key={day.key}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/10 rounded-xl p-4 border border-white/20"
          >
            <div className="flex items-center justify-between mb-3">
              <label className="text-white font-semibold capitalize text-base sm:text-lg">
                {day.label}
              </label>
              <label className="flex items-center gap-2 text-white/80 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hours[day.key].closed}
                  onChange={() => handleToggleClosed(day.key)}
                  className="w-4 h-4 rounded cursor-pointer"
                />
                <span className="text-sm sm:text-base font-medium">Closed</span>
              </label>
            </div>

            {!hours[day.key].closed && (
              <div className="flex items-center gap-2 sm:gap-3">
                <input
                  type="time"
                  value={hours[day.key].open}
                  onChange={(e) => handleTimeChange(day.key, 'open', e.target.value)}
                  className="flex-1 bg-white/10 border border-white/30 rounded-lg px-3 py-2.5 text-white text-sm sm:text-base focus:outline-none focus:border-white/50 focus:bg-white/15"
                />
                <span className="text-white/70 text-sm sm:text-base font-medium">to</span>
                <input
                  type="time"
                  value={hours[day.key].close}
                  onChange={(e) => handleTimeChange(day.key, 'close', e.target.value)}
                  className="flex-1 bg-white/10 border border-white/30 rounded-lg px-3 py-2.5 text-white text-sm sm:text-base focus:outline-none focus:border-white/50 focus:bg-white/15"
                />
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <div className="mt-4 sm:mt-6 flex justify-end">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSave}
          className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all text-sm sm:text-base shadow-lg ${
            saved
              ? 'bg-green-500 text-white'
              : 'bg-white text-slate-900 hover:bg-gray-100'
          }`}
        >
          <FiSave />
          <span>{saved ? 'Saved!' : 'Save Changes'}</span>
        </motion.button>
      </div>
    </div>
  );
};

export default HoursManager;